import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { OverlayPanel } from "primereact/overlaypanel";
import { Paginator } from "primereact/paginator";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import FilterDropdown, { createdDateOf, getDateRange, isCustomDatePeriod, isLifetimePeriod } from "../../Components/DateRange";
import { FormatCurrency, useCurrencyConstants } from "../../Components/CurrencyFormatter";
import { useDateFormatter } from "../../Components/FormatDateTime";
import BusinessCustomersActions from "../../Redux/transition/customers/business-customer.reducer";

const LIST_KEYS = ["entries", "lines", "items", "content", "statements", "statementEntries", "rows"];
const STATEMENT_PAGE_SIZES = [20, 50, 100, 200];
const DEFAULT_STATEMENT_PAGE_SIZE = STATEMENT_PAGE_SIZES[0];

const toPlain = (data) => {
    if (data == null) return data;
    if (typeof data.asMutable === "function") return data.asMutable({ deep: true });
    return data;
};

const asDate = (value) => {
    if (Array.isArray(value) && value.length >= 3) {
        const [year, month, day] = value;
        return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }
    return value;
};

const textOf = (value) => {
    if (value == null || value === "") return "";
    if (typeof value === "object") {
        return textOf(value.name ?? value.label ?? value.title ?? value.reference);
    }
    return String(value);
};

const firstDefined = (source, keys) => {
    if (!source || typeof source !== "object") return undefined;
    for (const key of keys) {
        const value = source[key];
        if (value != null && value !== "") return value;
    }
    return undefined;
};

const formatEntryType = (value) => {
    const text = textOf(value).trim();
    if (!text) return "";
    if (text !== text.toUpperCase() || /\s/.test(text)) return text;
    return text
        .toLowerCase()
        .split("_")
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
};

const RELATION_KEYS = ["relation", "relationType", "relationship", "sourceRelation", "payerRelation", "accountRelation"];

const relationOf = (item) => {
    const direct = firstDefined(item, RELATION_KEYS);
    if (direct != null) return direct;
    const typeRaw = firstDefined(item, ["entryType", "type"]);
    if (typeRaw && typeof typeRaw === "object") return firstDefined(typeRaw, RELATION_KEYS);
    return undefined;
};

const normalizeStatement = (payload, formatDate) => {
    const plain = toPlain(payload);
    if (plain == null) return { meta: [], summary: [], entries: [], totalElements: 0 };

    const { rows: listSource, totalElements } = resolveStatementList(plain);

    const entries = listSource.map((row, index) => {
        const item = toPlain(row) || {};
        return {
            id: item.id ?? `${index}`,
            receivedDate: asDate(firstDefined(item, ["receivedDate", "receivedOn", "receivedAt", "date"])),
            plan: textOf(firstDefined(item, ["planName", "plan", "productName"])),
            group: textOf(firstDefined(item, ["groupName", "group", "planGroupName", "planGroup"])),
            amount: firstDefined(item, ["amount", "value"]),
            entryType: firstDefined(item, ["entryType", "type", "source"]),
            relation: relationOf(item),
        };
    });

    if (Array.isArray(plain)) return { meta: [], summary: [], entries, totalElements };

    const summarySource = plain.summary && typeof plain.summary === "object" && !Array.isArray(plain.summary)
        ? plain.summary
        : (plain.totals && typeof plain.totals === "object" && !Array.isArray(plain.totals) ? plain.totals : plain);

    const summaryDefs = [
        { keys: ["paymentCount"], labelKey: "business_customers.statement_payment_count", money: false },
        { keys: ["onlineAmount"], labelKey: "business_customers.statement_online_amount", money: true },
        { keys: ["manualAmount"], labelKey: "business_customers.statement_manual_amount", money: true },
        { keys: ["fromSecondaries", "secondaryAmount", "secondariesAmount"], labelKey: "business_customers.statement_from_secondaries", money: true },
        { keys: ["fromLinkedPlans", "linkedPlanAmount", "linkedPlansAmount"], labelKey: "business_customers.statement_from_linked_plans", money: true },
        { keys: ["total", "totalAmount"], labelKey: "business_customers.statement_total", money: true },
    ];

    const summary = summaryDefs.flatMap((def) => {
        const value = firstDefined(summarySource, def.keys);
        if (value == null) return [];
        return [{ labelKey: def.labelKey, value, money: def.money }];
    });

    const customer = textOf(firstDefined(plain, ["customerName", "customer"]));
    const customerNo = textOf(firstDefined(plain, ["customerNum", "customerNo", "customerNumber"]));
    const start = firstDefined(plain, ["periodStart", "startDate"]);
    const end = firstDefined(plain, ["periodEnd", "endDate"]);
    const period = textOf(firstDefined(plain, ["period"]))
        || (start && end ? `${formatDate(asDate(start))} – ${formatDate(asDate(end))}` : "");

    const meta = [];
    if (customer) meta.push({ labelKey: "business_customers.statement_customer", value: customer });
    if (customerNo) meta.push({ labelKey: "business_customers.statement_customer_no", value: customerNo });
    if (period) meta.push({ labelKey: "business_customers.statement_period", value: period });

    return { meta, summary, entries, totalElements };
};

const hasAmount = (value) => value != null && String(value).trim() !== "";

const toCount = (value, fallback) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const pageOf = (value) => {
    if (!value || typeof value !== "object" || Array.isArray(value) || !Array.isArray(value.content)) {
        return null;
    }
    return value;
};

const resolveStatementList = (plain) => {
    if (Array.isArray(plain)) {
        return { rows: plain, totalElements: plain.length };
    }

    if (!plain || typeof plain !== "object") {
        return { rows: [], totalElements: 0 };
    }

    for (const key of LIST_KEYS) {
        const value = plain[key];
        if (Array.isArray(value)) {
            const pageMeta = plain.page && typeof plain.page === "object" ? plain.page : null;
            return {
                rows: value,
                totalElements: toCount(
                    firstDefined(plain, ["totalElements", "totalCount", "totalRecords"])
                        ?? (pageMeta ? firstDefined(pageMeta, ["totalElements", "totalCount", "totalRecords"]) : undefined),
                    value.length
                ),
            };
        }
        const nestedPage = pageOf(value);
        if (nestedPage) {
            return {
                rows: nestedPage.content,
                totalElements: toCount(
                    firstDefined(nestedPage, ["totalElements", "totalCount", "totalRecords"]),
                    nestedPage.content.length
                ),
            };
        }
    }

    const topPage = pageOf(plain);
    if (topPage) {
        return {
            rows: topPage.content,
            totalElements: toCount(
                firstDefined(topPage, ["totalElements", "totalCount", "totalRecords"]),
                topPage.content.length
            ),
        };
    }

    return { rows: [], totalElements: 0 };
};

const StatementOfAccountTab = ({ customerId }) => {
    const { t } = useTranslation("common");
    const { currencySymbol, currencyFormat } = useCurrencyConstants();
    const dispatch = useDispatch();
    const { formatApiDate, formatDate } = useDateFormatter();
    const toast = useRef(null);
    const exportMenuRef = useRef(null);
    const exportRequestRef = useRef(null);
    const listRequestRef = useRef(null);

    const {
        fetchingBusinessCustomerStatement,
        fetchingBusinessCustomerStatementCsv,
        fetchingBusinessCustomerStatementPdf,
        businessCustomerStatement,
        businessCustomerStatementCsv,
        businessCustomerStatementPdf,
        businessCustomerStatementError,
        businessCustomerStatementCsvError,
        businessCustomerStatementPdfError,
    } = useSelector((reduxState) => reduxState.businessCustomers);

    const [period, setPeriod] = useState("Last 12 months");
    const [calendarDates, setCalendarDates] = useState(null);
    const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
    const [statementView, setStatementView] = useState(null);
    const [loadingView, setLoadingView] = useState(true);
    const [viewError, setViewError] = useState(false);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(DEFAULT_STATEMENT_PAGE_SIZE);
    const [totalRecords, setTotalRecords] = useState(0);

    const customerSummary = useSelector((reduxState) => reduxState.businessCustomers.customerSummary);
    const businessCustomerRecord = useSelector((reduxState) => reduxState.businessCustomers.businessCustomerRecord);
    const lifetimeStart = useMemo(
        () => createdDateOf(customerSummary?.businessCustomer) || createdDateOf(businessCustomerRecord),
        [customerSummary, businessCustomerRecord]
    );

    const statementPeriods = useMemo(() => ([
        { code: "Today", value: t("dashboard.today") },
        { code: "Last week", value: t("dashboard.last_week") },
        { code: "This month", value: t("dashboard.this_month") },
        { code: "Last month", value: t("dashboard.last_month") },
        { code: "Last 3 months", value: t("dashboard.3_months_duration") },
        { code: "Last 6 months", value: t("dashboard.6_months_duration") },
        { code: "Last 12 months", value: t("dashboard.12_months_duration") },
        { code: "Year to date", value: t("dashboard.year_to_date") },
        { code: "Lifetime", value: t("dashboard.lifetime") },
        { code: "Custom date", value: t("dashboard.custom_date") },
    ]), [t]);

    const getStatement = useCallback(
        (businessCustomerId, startDate, endDate, options) =>
            dispatch(BusinessCustomersActions.businessCustomerStatementRequest(businessCustomerId, startDate, endDate, options)),
        [dispatch]
    );
    const getStatementCsv = useCallback(
        (businessCustomerId, startDate, endDate) =>
            dispatch(BusinessCustomersActions.businessCustomerStatementCsvRequest(businessCustomerId, startDate, endDate)),
        [dispatch]
    );
    const getStatementPdf = useCallback(
        (businessCustomerId, startDate, endDate) =>
            dispatch(BusinessCustomersActions.businessCustomerStatementPdfRequest(businessCustomerId, startDate, endDate)),
        [dispatch]
    );

    const loadStatement = useCallback((startDate, endDate, nextPage = 0, nextSize = DEFAULT_STATEMENT_PAGE_SIZE, { clear = false } = {}) => {
        if (!customerId || !startDate || !endDate) return;
        listRequestRef.current = { startDate, endDate, page: nextPage, size: nextSize, sawFetching: false };
        setLoadingView(true);
        setViewError(false);
        if (clear) {
            setStatementView(null);
            setTotalRecords(0);
        }
        getStatement(customerId, startDate, endDate, { page: nextPage, size: nextSize });
    }, [customerId, getStatement]);

    useEffect(() => {
        if (!customerId) {
            setLoadingView(false);
            return undefined;
        }
        const { startDate, endDate } = getDateRange("Last 12 months", { returnRange: true });
        const nextRange = {
            startDate: formatApiDate(startDate),
            endDate: formatApiDate(endDate),
        };
        setPeriod("Last 12 months");
        setCalendarDates(null);
        setDateRange(nextRange);
        setFirst(0);
        setRows(DEFAULT_STATEMENT_PAGE_SIZE);
        loadStatement(nextRange.startDate, nextRange.endDate, 0, DEFAULT_STATEMENT_PAGE_SIZE, { clear: true });
        return undefined;
        // formatApiDate is recreated each render; load once per customer.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId]);

    useEffect(() => {
        const request = listRequestRef.current;
        if (!request) return undefined;
        if (fetchingBusinessCustomerStatement) {
            request.sawFetching = true;
            return undefined;
        }
        if (!request.sawFetching) return undefined;

        listRequestRef.current = null;
        if (businessCustomerStatementError) {
            setStatementView(null);
            setTotalRecords(0);
            setViewError(true);
            setLoadingView(false);
            return undefined;
        }

        const nextView = normalizeStatement(businessCustomerStatement ?? [], formatDate);
        setStatementView((current) => {
            if (!current) return nextView;
            return {
                ...nextView,
                meta: nextView.meta.length > 0 ? nextView.meta : current.meta,
                summary: nextView.summary.length > 0 ? nextView.summary : current.summary,
            };
        });
        setTotalRecords(nextView.totalElements ?? nextView.entries.length);
        setViewError(false);
        setLoadingView(false);
        return undefined;
    }, [
        fetchingBusinessCustomerStatement,
        businessCustomerStatement,
        businessCustomerStatementError,
        formatDate,
    ]);

    const downloadBlob = useCallback((data, filename, mimeType) => {
        const blob = data instanceof Blob ? data : new Blob([data], { type: mimeType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    }, []);

    const beginExport = useCallback((kind) => {
        exportMenuRef.current?.hide();
        if (!customerId || !dateRange.startDate || !dateRange.endDate) {
            toast.current?.show({
                severity: "warn",
                summary: t("error.error"),
                detail: t("business_customers.statement_date_required"),
                life: 3000,
            });
            return;
        }
        exportRequestRef.current = {
            kind,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            sawFetching: false,
        };
        if (kind === "pdf") {
            getStatementPdf(customerId, dateRange.startDate, dateRange.endDate);
            return;
        }
        getStatementCsv(customerId, dateRange.startDate, dateRange.endDate);
    }, [customerId, dateRange.endDate, dateRange.startDate, getStatementCsv, getStatementPdf, t]);

    useEffect(() => {
        const request = exportRequestRef.current;
        if (!request) return undefined;

        const exportInFlight = request.kind === "pdf"
            ? fetchingBusinessCustomerStatementPdf
            : fetchingBusinessCustomerStatementCsv;
        if (exportInFlight) {
            request.sawFetching = true;
            return undefined;
        }
        if (!request.sawFetching) return undefined;

        if (request.kind === "pdf") {
            exportRequestRef.current = null;
            if (businessCustomerStatementPdf) {
                downloadBlob(
                    businessCustomerStatementPdf,
                    `customer_statement_of_accounts_${request.startDate}_${request.endDate}.pdf`,
                    "application/pdf"
                );
                toast.current?.show({
                    severity: "success",
                    summary: t("success"),
                    detail: t("business_customers.statement_pdf_download_success"),
                    life: 3000,
                });
            } else if (businessCustomerStatementPdfError) {
                toast.current?.show({
                    severity: "error",
                    summary: t("error.error"),
                    detail: t("business_customers.statement_pdf_download_error"),
                    life: 3000,
                });
            }
            return undefined;
        }

        exportRequestRef.current = null;

        if (businessCustomerStatementCsv) {
            downloadBlob(
                businessCustomerStatementCsv,
                `customer_statement_of_accounts_${request.startDate}_${request.endDate}.csv`,
                "text/csv"
            );
            toast.current?.show({
                severity: "success",
                summary: t("success"),
                detail: t("business_customers.statement_csv_download_success"),
                life: 3000,
            });
        } else if (businessCustomerStatementCsvError) {
            toast.current?.show({
                severity: "error",
                summary: t("error.error"),
                detail: t("business_customers.statement_csv_download_error"),
                life: 3000,
            });
        }
        return undefined;
    }, [
        fetchingBusinessCustomerStatementCsv,
        fetchingBusinessCustomerStatementPdf,
        businessCustomerStatementCsv,
        businessCustomerStatementPdf,
        businessCustomerStatementCsvError,
        businessCustomerStatementPdfError,
        downloadBlob,
        t,
    ]);

    const applyRange = (startDate, endDate) => {
        setDateRange({ startDate, endDate });
        setFirst(0);
        loadStatement(startDate, endDate, 0, rows, { clear: true });
    };

    const onPageChange = (event) => {
        const nextSize = event.rows;
        const sizeChanged = nextSize !== rows;
        const nextPage = sizeChanged ? 0 : event.page;
        setRows(nextSize);
        setFirst(sizeChanged ? 0 : event.first);
        loadStatement(dateRange.startDate, dateRange.endDate, nextPage, nextSize);
    };

    const handlePeriodChange = (event) => {
        const nextPeriod = event.value;
        setPeriod(nextPeriod);
        if (isCustomDatePeriod(nextPeriod)) {
            setCalendarDates(null);
            setDateRange({ startDate: null, endDate: null });
            setStatementView(null);
            setTotalRecords(0);
            setFirst(0);
            setViewError(false);
            setLoadingView(false);
            listRequestRef.current = null;
            return;
        }
        const { startDate, endDate } = getDateRange(nextPeriod, { returnRange: true, lifetimeStart });
        if (!startDate || !endDate) return;
        setCalendarDates(null);
        applyRange(formatApiDate(startDate), formatApiDate(endDate));
    };

    useEffect(() => {
        if (!isLifetimePeriod(period) || !lifetimeStart) return;
        const { startDate, endDate } = getDateRange(period, { returnRange: true, lifetimeStart });
        if (!startDate || !endDate) return;
        const nextStart = formatApiDate(startDate);
        const nextEnd = formatApiDate(endDate);
        if (dateRange.startDate === nextStart && dateRange.endDate === nextEnd) return;
        applyRange(nextStart, nextEnd);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [period, lifetimeStart]);

    const handleCalendarSelect = (event) => {
        const range = event.value;
        setCalendarDates(range);
        if (Array.isArray(range) && range[0] && range[1]) {
            applyRange(formatApiDate(range[0]), formatApiDate(range[1]));
        }
    };

    const canExport = Boolean(dateRange.startDate && dateRange.endDate);
    const exporting = fetchingBusinessCustomerStatementCsv || fetchingBusinessCustomerStatementPdf;
    const entries = statementView?.entries || [];

    const amountBody = (value, className = "") => (
        hasAmount(value) ? (
            <FormatCurrency
                amount={value}
                currencySymbol={currencySymbol}
                currencyFormat={currencyFormat}
                className={className}
            />
        ) : ""
    );

    const nameStack = (primary, secondary) => (
        <div className="subscriptionMainBodyTemplate subscriptionBodyTemplate_customers statement-of-account-tab__plan">
            {primary ? <span className="tier-name-content">{primary}</span> : null}
            {secondary ? <span className="plan-text">{secondary}</span> : null}
        </div>
    );

    const typeRelationBody = (row) => {
        const type = formatEntryType(row.entryType);
        const relation = formatEntryType(row.relation);
        if (type && relation && type !== relation) return nameStack(type, relation);
        return nameStack(type || relation, "");
    };

    const pageSizeOptions = STATEMENT_PAGE_SIZES.map((value) => ({ label: String(value), value }));
    const paginatorTemplate = {
        layout: "RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport",
        RowsPerPageDropdown: () => (
            <div className="p-d-flex p-ai-center p-mr-3 p-2">
                <span style={{ display: "flex", alignSelf: "center" }}>
                    {t("business_customers.item_labels")}{":"}{" "}
                </span>
                <Dropdown
                    value={rows}
                    options={pageSizeOptions}
                    onChange={(event) => {
                        if (event.value === rows) return;
                        setRows(event.value);
                        setFirst(0);
                        loadStatement(dateRange.startDate, dateRange.endDate, 0, event.value);
                    }}
                    style={{ width: "70px", minWidth: "70px", border: "1px solid #495057" }}
                    panelClassName="custom-dropdown-panel"
                />
            </div>
        ),
    };

    return (
        <div className="statement-of-account-tab">
            <Toast ref={toast} />
            <div className="statement-of-account-tab__toolbar">
                <div className="statement-of-account-tab__period">
                    <p className="statement-of-account-tab__hint">{t("business_customers.statement_period_hint")}</p>
                    <FilterDropdown
                        value={period}
                        options={statementPeriods}
                        onChange={handlePeriodChange}
                        optionLabel="value"
                        optionValue="code"
                        placeholder={t("plan.reporting_period_hint")}
                        className="activity-dropdown"
                        containerClass="statement-of-account-tab__period-field"
                        containerStyle={{ padding: 0, margin: 0, width: "20rem", maxWidth: "100%" }}
                        style={{ textAlign: "left", width: "100%" }}
                        id="periodtype"
                    />
                    {isCustomDatePeriod(period) ? (
                        <Calendar
                            value={calendarDates}
                            onChange={handleCalendarSelect}
                            selectionMode="range"
                            readOnlyInput
                            hideOnRangeSelection
                            showIcon
                            showButtonBar
                            dateFormat="dd M yy"
                            maxDate={new Date()}
                            placeholder={t("report.choose_date")}
                            className="statement-of-account-tab__calendar"
                        />
                    ) : null}
                </div>
                <div className="statement-of-account-tab__downloads">
                    <Button
                        label={t("export")}
                        icon={exporting ? "pi pi-spin pi-spinner" : "pi pi-download"}
                        className="p-button-outlined"
                        disabled={!canExport || exporting}
                        aria-haspopup="true"
                        onClick={(event) => exportMenuRef.current?.toggle(event)}
                    />
                    <OverlayPanel
                        ref={exportMenuRef}
                        className="custom-overlay subscriber-statement-menu"
                        style={{ backgroundColor: "#fff" }}
                    >
                        <ul className="user-profile-items subscriber-detail-submenu__list">
                            <li>
                                <div className="p-link list-button">
                                    <span
                                        className="list-items subscriber-statement-menu__item"
                                        onClick={() => beginExport("pdf")}
                                    >
                                        <i className="pi pi-file-pdf" aria-hidden="true" />
                                        {t("business_customers.statement_pdf")}
                                    </span>
                                </div>
                            </li>
                            <li>
                                <div className="p-link list-button">
                                    <span
                                        className="list-items subscriber-statement-menu__item"
                                        onClick={() => beginExport("csv")}
                                    >
                                        <i className="pi pi-file" aria-hidden="true" />
                                        {t("business_customers.statement_csv")}
                                    </span>
                                </div>
                            </li>
                        </ul>
                    </OverlayPanel>
                </div>
            </div>

            {!statementView && loadingView ? (
                <div className="statement-of-account-tab__status">
                    <ProgressSpinner style={{ width: "40px", height: "40px" }} />
                </div>
            ) : !statementView && viewError ? (
                <p className="statement-of-account-tab__status">{t("business_customers.statement_load_error")}</p>
            ) : !statementView ? (
                <p className="statement-of-account-tab__status">{t("business_customers.statement_date_required")}</p>
            ) : (
                <>
                    {statementView.meta.length > 0 && (
                        <div className="statement-of-account-tab__meta">
                            {statementView.meta.map(({ labelKey, value }) => (
                                <div className="statement-of-account-tab__meta-item" key={labelKey}>
                                    <span className="statement-of-account-tab__meta-label">{t(labelKey)}</span>
                                    <span className="statement-of-account-tab__meta-value">{value || ""}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    {statementView.summary.length > 0 && (
                        <div className="statement-of-account-tab__summary">
                            {statementView.summary.map(({ labelKey, value, money }) => (
                                <div className="statement-of-account-tab__summary-item" key={labelKey}>
                                    <span className="statement-of-account-tab__meta-label">{t(labelKey)}</span>
                                    <span className="statement-of-account-tab__meta-value">
                                        {money ? amountBody(value) : (value ?? "")}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className={`statement-of-account-tab__table${loadingView ? " is-loading" : ""}`}>
                        {loadingView ? (
                            <div className="statement-of-account-tab__loader" aria-live="polite" aria-busy="true">
                                <ProgressSpinner style={{ width: "40px", height: "40px" }} />
                            </div>
                        ) : null}
                        <DataTable
                            value={entries}
                            className="p-datatable-responsive-table datatable statement-of-account-tab__entries"
                            emptyMessage={t("subscriber.no_statement_rows")}
                        >
                            <Column
                                field="receivedDate"
                                header={t("business_customers.statement_received_date")}
                                body={(row) => (
                                    row.receivedDate ? (
                                        <span className="statement-entry__primary">{formatDate(row.receivedDate)}</span>
                                    ) : ""
                                )}
                                align="left"
                                alignHeader="left"
                                headerClassName="datatable-cell-default datatable-header-default statement-of-account-tab__date"
                                className="datatable-cell-default statement-of-account-tab__date"
                                bodyClassName="datatable-cell-default statement-of-account-tab__date"
                            />
                            <Column
                                field="plan"
                                header={t("business_customers.statement_plan")}
                                body={(row) => nameStack(row.plan, row.group)}
                                align="left"
                                alignHeader="left"
                                headerClassName="datatable-cell-default datatable-header-default"
                                className="datatable-cell-default"
                                bodyClassName="datatable-cell-default"
                            />
                            <Column
                                field="entryType"
                                header={t("business_customers.statement_type_relation")}
                                body={typeRelationBody}
                                align="left"
                                alignHeader="left"
                                headerClassName="datatable-cell-default datatable-header-default"
                                className="datatable-cell-default"
                                bodyClassName="datatable-cell-default"
                            />
                            <Column
                                field="amount"
                                header={t("amount")}
                                body={(row) => amountBody(row.amount, "statement-entry__primary")}
                                align="left"
                                alignHeader="left"
                                headerClassName="datatable-cell-default datatable-header-default"
                                className="datatable-cell-default"
                                bodyClassName="datatable-cell-default"
                            />
                        </DataTable>
                    </div>
                    <Paginator
                        first={first}
                        rows={rows}
                        totalRecords={totalRecords}
                        onPageChange={onPageChange}
                        template={paginatorTemplate}
                        currentPageReportTemplate="{first} - {last} of {totalRecords}"
                        className="custom-paginator statement-of-account-tab__paginator"
                    />
                </>
            )}
        </div>
    );
};

export default StatementOfAccountTab;
